import { type ClientErrorKind, type ClientErrorReport, reportablePath } from '@boardflight/shared';
import { anonymous } from './api';
import { BUILD_META } from './build-version';
import { findShell } from './storefront/link/bridge';

/**
 * A page telling BoardFlight that it broke (`POST /v1/client/errors`), so an operator hears of it the
 * way they hear of a 500, without anybody having to write in.
 *
 * Only this site's own failures go: an error whose stack is not in a script this origin served is a
 * browser extension's or an embedded page's, and a fetch that never reached the server is somebody's
 * Wi-Fi. Nothing names who saw it, and the path is cut down by `reportablePath` before it leaves.
 * A page sends at most a few, once each, however often the same thing throws.
 */

const MAX_PER_PAGE = 5;
const sent = new Set<string>();

/** Failures that are the network's, not the code's: each browser words a dropped fetch its own way. */
const NOT_OURS = [
  /^Failed to fetch$/i,
  /^Load failed$/i,
  /^NetworkError when attempting to fetch resource\.?$/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /^ResizeObserver loop/i,
];

function pageBuild(): string | null {
  try {
    return document.querySelector(`meta[name="${BUILD_META}"]`)?.getAttribute('content') || null;
  } catch {
    return null;
  }
}

/** Report one failure. Never throws, and never reports its own. */
export function reportPageError(kind: ClientErrorKind, error: unknown, filename?: string): void {
  try {
    const failure = error instanceof Error ? error : null;
    const message = (failure ? `${failure.name}: ${failure.message}` : String(error)).slice(0, 500);
    const stack = failure?.stack?.slice(0, 2000) ?? null;
    const origin = window.location.origin;
    const ours = (stack ?? '').includes(origin) || (filename ?? '').startsWith(origin);
    if (!ours || NOT_OURS.some((pattern) => pattern.test(failure?.message ?? message))) return;
    if (sent.size >= MAX_PER_PAGE || sent.has(message)) return;
    sent.add(message);

    const report: ClientErrorReport = {
      kind,
      message,
      stack,
      path: reportablePath(window.location.pathname),
      build: pageBuild(),
      surface: findShell() !== null ? 'desktop' : 'web',
    };
    void anonymous<void>('/v1/client/errors', {
      method: 'POST',
      body: JSON.stringify(report),
    }).catch(() => undefined);
  } catch {
    // A reporter that fails is one report fewer, never a second failure.
  }
}

/** Listen for what nothing else caught. Once, before the first render. */
export function startErrorReporting(): void {
  window.addEventListener('error', (event) => reportPageError('error', event.error ?? event.message, event.filename));
  window.addEventListener('unhandledrejection', (event) => reportPageError('rejection', event.reason));
}
